import React, { useRef, useState, useEffect } from 'react';
import { Button, Card, Container, Row, Col, Spinner, Form, Table, Badge, Pagination as BsPagination } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';
import { toast, ToastContainer } from 'react-toastify';
import ExcelIcon from '../../Assets/ExcelIcon.svg';
import moment from 'moment';
import { useSelector } from 'react-redux';

const TelecallingDefaulter = () => {

  const token = useSelector(state => state.loginData.userData.token);
  const userData = useSelector(state => state.loginData.userData);
  // NOTE: verify the actual field name for the logged-in user's display name in your redux store
  const uploadedBy = userData?.fullName || userData?.name || userData?.userName || "";

  const selectedFinancialYear = useSelector((state) => state.activeData.financialYear);
  const formattedFinancialYear = `${selectedFinancialYear.slice(0, 5)}${selectedFinancialYear.slice(7)}`;

  const [error, setError] = useState("");
  const [excelFile, setExcelFile] = useState(null);
  const fileInputRef = useRef(null);
  const [dataFilterOptions, setDataFilterOptions] = useState([]);
  const [dataFilter, setDataFilter] = useState('');
  const [dataFilterErr, setDataFilterErr] = useState(false);
  const [loader, setLoader] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  // ── Logs table state ────────────────────────────────────────────────────
  const [logsData, setLogsData] = useState([]);
  const [logsLoader, setLogsLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [docsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  // ─────────────────────────────────────────────────────────────────────

  useEffect(() => {
    getDataFilterOptions();
  }, []);

  useEffect(() => {
    getUploadLogs();
  }, [currentPage]);

  const getDataFilterOptions = async () => {
    try {
      const res = await fetch(process.env.REACT_APP_BASE_URL + '/tc/callingFilterList', {
        method: 'GET'
      });

      const response = await res.json();
      setDataFilterOptions(response.data);
    } catch (Err) {
      console.log("Err while getting DataFilterOptions", Err)
    }
  }

  // ── Fetch upload logs (paginated) ───────────────────────────────────────
  const getUploadLogs = async (page = currentPage) => {
    setLogsLoader(true);
    try {
      const res = await fetch(process.env.REACT_APP_BASE_URL + '/datadump/uploadTeleCallingDataLogsReadPgn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          documentsPerPage: docsPerPage,
          page: page,
        }),
      });

      const response = await res.json();

      if (response.status === 200) {
        setLogsData(response.data || []);
        setTotalPages(response.noOfPages || 0);
      } else {
        setLogsData([]);
        setTotalPages(0);
      }
    } catch (err) {
      console.error("Err while getting upload logs", err);
      setLogsData([]);
      setTotalPages(0);
    } finally {
      setLogsLoader(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────

  const handleFileUpload = async (event) => {

    const file = event.target.files[0];
    if (!file) return;

    // Check file type — Excel or CSV
    const validExtensions = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      'application/csv',
      'application/vnd.ms-excel.sheet.macroEnabled.12',
    ];
    const validFileExtensions = ['.xls', '.xlsx', '.csv'];

    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(file.type) && !validFileExtensions.includes(fileExtension)) {
      setError("Please upload an Excel or CSV file.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Just store the file locally — actual upload happens on Submit click
    setExcelFile(file);
    setError("");
  };

  const handleRemoveFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // ✅ Reset before processing
    }
    setExcelFile(null);
    setError("");
  }

  // ── Submit → upload file to chunked queue API ───────────────────────────
  const submitTeleCalling = async () => {
    if (!dataFilter) {
      setDataFilterErr(true);
      return;
    }

    if (!excelFile) {
      setError("Please upload excel file");
      return;
    }

    if (error.length > 0) {
      setSubmitError("Please resolve above error before submiting.");
      return;
    }

    try {
      setLoader(true);
      setSubmitError(false);

      const formData = new FormData();
      formData.append("file", excelFile);
      formData.append("uploadedBy", uploadedBy);
      formData.append("dataFilter", dataFilter);

      const res = await fetch(process.env.REACT_APP_BASE_URL + '/datadump/uploadTeleCallingDataInChunksByQueue', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const response = await res.json();

      if (res.ok && response.status !== 400 && response.status !== 500) {
        toast.success(response.message || "Telecalling data upload started successfully");
        setCurrentPage(1);
        getUploadLogs(1);
      } else {
        toast.error(response.message || "Failed, please try again");
      }
    }
    catch (Err) {
      console.log("Err while telecalling defaulter", Err);
      toast.error("Failed, please try again");
    }
    finally {
      setLoader(false);
      setExcelFile(null);
      setError("");
      setDataFilterErr(false);
      setDataFilter("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  // ── Status badge color helper ───────────────────────────────────────────
  const getStatusBadge = (status) => {
    const normalized = (status || "").toLowerCase();
    if (normalized === "completed") return <Badge bg="success">Completed</Badge>;
    if (normalized === "failed" || normalized === "error") return <Badge bg="danger">{status}</Badge>;
    if (normalized === "processing" || normalized === "pending" || normalized === "in progress") return <Badge bg="primary">{status}</Badge>;
    return <Badge bg="secondary">{status || "-"}</Badge>;
  };

  return (
    <div className='TelecallingDefaulter'>
      <ToastContainer />

      <Container>
        <div className='d-flex justify-content-center align-items-center'>
          <h3 className='mb-0'>TeleCalling Defaulter (Internal Dev)</h3>
        </div>

        <Row className='justify-content-end me-4'>
          <Button
            href=" https://newptax.s3.ap-south-1.amazonaws.com/pcmcPTax/telecalling_samplefile.csv" // Make sure the file is inside the "public" folder
            download="Telecalling_Sample_Excel.xlsx"
            className="btn-submit ms-auto w-auto">
            <Icon.ArrowDownCircle className='me-2' /> Download Sample Excel
          </Button>
        </Row>

        <Row className='justify-content-center my-4'>
          <Col md={4}>
            <Card>
              <Card.Header className='py-3'>
                <h5 className='text-center mb-0'>Upload Bulk Excel</h5>
              </Card.Header>

              <Card.Body className='px-4'>
                <div className='mt-2 mb-3 justify-content-center'>
                  <Form.Label><span className='me-1'>*</span> Data Type</Form.Label>

                  <Form.Select value={dataFilter}
                    onChange={(e) => { setDataFilter(e.target.value); setExcelFile(null); setError(""); setDataFilterErr(false) }}>
                    <option value="" disabled>
                      {" "}
                      Select Data Type
                    </option>
                    {dataFilterOptions !== undefined && dataFilterOptions.length > 0 ? (
                      dataFilterOptions.map((row, idx) => {
                        return (
                          <option key={idx} value={row.value}>{row.label} </option>
                        );
                      })
                    ) : (
                      <option value="">No Data Found</option>
                    )}
                  </Form.Select>
                  {dataFilterErr && <p className='errMsg'>Please select data type to proceed</p>}
                </div>
                {
                  dataFilter &&

                  <div className='mt-4 mb-2 d-flex justify-content-center'>
                    {
                      excelFile !== null ? (
                        <Card className='uploadedFile text-center px-3 py-3 mx-auto'>
                          <img src={ExcelIcon} width={40} className='mx-auto mb-2' alt="Excel file" />
                          <p className='mb-0'>{excelFile?.name}</p>
                          <Icon.XCircleFill className='removeIcon' size={18}
                            onClick={handleRemoveFile} />
                        </Card>
                      ) :

                        <div>
                          <input type="file" id="fileInput"
                            ref={fileInputRef}
                            accept=".xlsx, .xls, .csv"
                            style={{ display: "none" }}
                            onChange={(e) => handleFileUpload(e)} />

                          <label htmlFor="fileInput" className="uploadFile" style={{ cursor: 'pointer' }}>
                            <Icon.CloudUpload className='me-2' />Click here to upload file
                          </label>

                        </div>
                    }

                  </div>
                }

                {error && <p className='errMsg' dangerouslySetInnerHTML={{ __html: error }} />}
              </Card.Body>

              <Card.Footer>
                {submitError && <p className='errMsg text-center mb-1'>{submitError}</p>}
                {
                  loader ? <center><Spinner /></center> :
                    <Button className='btn-submit ms-auto' onClick={submitTeleCalling}>
                      <Icon.Send className='me-2' size={14} /> Submit
                    </Button>
                }
              </Card.Footer>
            </Card>
          </Col>
        </Row>

        {/* ── Upload Logs Table ─────────────────────────────────────────── */}
        <Row className='justify-content-center my-4'>
          <Col md={10}>
            <Card className="shadow-sm rounded-4 overflow-hidden">
              <Card.Header className="py-3 text-center" style={{ background: "#1a4db3", color: "#fff" }}>
                <Icon.ClockHistory size={18} className="me-2" />
                <span className="fw-semibold">Upload History</span>
              </Card.Header>

              <Card.Body className="p-0">
                {logsLoader ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner animation="border" style={{ color: "#1a4db3" }} />
                  </div>
                ) : logsData && logsData.length > 0 ? (
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                      <thead style={{ background: "#f0f4ff" }}>
                        <tr>
                          <th className="px-3 py-2">Uploaded By</th>
                          <th className="px-3 py-2">Data Filter</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Processed Count</th>
                          <th className="px-3 py-2">Uploaded At</th>
                          <th className="px-3 py-2">Completed At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logsData.map((item, index) => (
                          <tr key={item._id || index}>
                            <td className="px-3 py-2">{item?.uploadedBy || "-"}</td>
                            <td className="px-3 py-2">{item?.dataFilter || "-"}</td>
                            <td className="px-3 py-2">{getStatusBadge(item?.status)}</td>
                            <td className="px-3 py-2">
                              {(item?.proccessedCount ?? item?.processedCount ?? 0).toLocaleString()}
                            </td>
                            <td className="px-3 py-2">
                              {item?.uploadedAt ? moment(item.uploadedAt).format("DD MMM YYYY, hh:mm A") : "-"}
                            </td>
                            <td className="px-3 py-2">
                              {item?.completedAt ? moment(item.completedAt).format("DD MMM YYYY, hh:mm A") : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="d-flex justify-content-center align-items-center py-5 text-muted">
                    No upload history found.
                  </div>
                )}
              </Card.Body>

              {totalPages > 1 && (
                <Card.Footer className="d-flex justify-content-center py-3">
                  <BsPagination className="mb-0">
                    <BsPagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    />
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                      <BsPagination.Item
                        key={pageNum}
                        active={pageNum === currentPage}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </BsPagination.Item>
                    ))}
                    <BsPagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    />
                  </BsPagination>
                </Card.Footer>
              )}
            </Card>
          </Col>
        </Row>
        {/* ─────────────────────────────────────────────────────────────── */}

      </Container>
    </div>
  )
}

export default TelecallingDefaulter;